/**
 * Which readers can enrol a fingerprint, and what a failed capture means.
 *
 * Both rules are silent when wrong: a too-narrow device filter hides a real reader from
 * Biometria, and a mis-read conflict hides the "Forçar recadastro" button that is the
 * only way out of a duplicate-fingerprint lockout.
 */

/** Synthetic id for the locally-attached IDBio Pro reader (USB, not a networked device). */
export const IDBIO_DEVICE_ID = '__idbio__';

export interface FingerprintDeviceLike {
  device_type?: string | null;
  manufacturer?: string | null;
  model?: string | null;
}

const lower = (value?: string | null): string => (value || '').toLowerCase();

/**
 * Whether this device can enrol fingerprints.
 *
 * Two ways to qualify: an explicit `fingerprint_scanner` device type, or a ControlID
 * iDFlex, whose line reports a generic type and so must be recognised by
 * manufacturer + model.
 */
export function isFingerprintDevice(device: FingerprintDeviceLike): boolean {
  const type = lower(device?.device_type);
  const manufacturer = lower(device?.manufacturer);
  const model = lower(device?.model);
  return type === 'fingerprint_scanner' || (manufacturer === 'controlid' && model.includes('idflex'));
}

/** The generic message shown when the reader gave no usable reason. */
const GENERIC_CAPTURE_ERROR = 'Erro ao capturar digital';

export interface CaptureErrorInput {
  /** Id of the device the wizard is enrolling on. */
  deviceId?: string | null;
  /** Error text reported by the reader, if any. */
  captureError?: string | null;
  /** Reader-internal id this finger collided with, or null when there was no collision. */
  conflictingReaderId?: number | null;
}

export interface CaptureErrorClassification {
  /** True when there is nothing more specific to say than the generic message. */
  isGenericFallback: boolean;
  /** The reader's own message, when it adds something beyond the generic one. */
  detail: string | null;
  /** The finger is already enrolled in the reader's internal database. */
  isDuplicateFingerprint: boolean;
  /** Whether the "Forçar recadastro" recovery can be offered. */
  canForceReenroll: boolean;
}

/**
 * Classifies a failed capture into what the operator should be shown.
 *
 * Note `conflictingReaderId` is compared against null explicitly: reader id 0 is a valid
 * id, and a truthiness check would hide both the explanation and the recovery button for
 * whoever happens to occupy the reader's first slot.
 */
export function classifyCaptureError(input: CaptureErrorInput): CaptureErrorClassification {
  const captureError = input?.captureError ?? '';
  const isGenericFallback = !captureError || captureError === GENERIC_CAPTURE_ERROR;
  const isDuplicateFingerprint = (input?.conflictingReaderId ?? null) !== null;
  // Forcing a re-enrol deletes the reader's internal user record, which only the
  // locally-attached IDBio path can do.
  const isIdbio = input?.deviceId === IDBIO_DEVICE_ID;

  return {
    isGenericFallback,
    detail: isGenericFallback ? null : captureError,
    isDuplicateFingerprint,
    canForceReenroll: isDuplicateFingerprint && isIdbio,
  };
}
